<script setup lang="ts">
import type { ItemType } from '~/types'

const favourites = useFavouritesStore()

type Props = {
  id: string
  type: ItemType
  to: string
  title: string
  image: {
    src?: string | null
    alt?: string | null
  }
  favourited?: string | null
  refetch?: () => Promise<unknown>
  class?: string
}

const props = withDefaults(defineProps<Props>(), {
  favourited: undefined,
  refetch: undefined,
  class: undefined,
})

const classes = computed(() => ({
  'app-card-large': true,
  [`${props.class}`]: !!props.class,
}))

const toggleFavourite = async (event: MouseEvent) => {
  event.preventDefault()

  await favourites.toggleFavourite({
    id: props.id,
    type: props.type,
    favouriteId: props.favourited,
  })

  if (props.refetch) await props.refetch()
}
</script>

<template>
  <AppPaper as="article" :class="classes">
    <div class="row g-0 md:flex-row-reverse">
      <div class="col-md-6">
        <NuxtLink :to="to">
          <AppImage
            class="app-card-large__image h-full min-h-[26.25rem] md:!rounded-none"
            ratio="16:10"
            :src="image.src"
            :alt="image.alt"
          >
            <AppButtonLike
              class="app-card-large__like-button"
              :active="!!favourited"
              @click="toggleFavourite"
            />
          </AppImage>
        </NuxtLink>
      </div>

      <div class="col-md-6">
        <div class="app-card-large__content">
          <h3 class="app-card-large__content-title h3 text-balance">
            {{ title }}
          </h3>

          <div v-if="$slots['meta']" class="app-card-large__content-meta">
            <slot name="meta"></slot>
          </div>

          <div class="app-card-large__content-text clean-cms-styles mb-auto">
            <slot></slot>
          </div>

          <AppButton :to="to" variant="text" class="mt-3">
            {{ $t('string.More-info') }}
            <template #icon-end-circle>
              <IconChevronRight />
            </template>
          </AppButton>
        </div>
      </div>
    </div>
  </AppPaper>
</template>

<style lang="scss">
@use 'sass:math';

@import '@boats/ui/styles/config';

.app-card-large .app-card-large__image {
  width: 100%;
  border-bottom-right-radius: 0;
  border-bottom-left-radius: 0;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    display: block;
    width: 100%;
    height: 100%;
    background-color: $black;
    opacity: 0;
    @include transition('opacity');
  }

  img {
    @include transition('transform');
  }

  // @include media-breakpoint-up(md) {
  //   width: 6.75rem;
  //   margin-right: 1.5rem;
  // }
}

.app-card-large__content {
  flex: 1 1 0%;
  padding: 0.75rem;

  @include media-breakpoint-up(md) {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    min-height: 100%;
    padding: 1.75rem;
  }
}

.app-card-large__content-title {
  margin-bottom: 0.75rem;

  // @include media-breakpoint-up(md) {
  //   margin-bottom: 0.5rem;
  // }
}

.app-card-large__content-meta {
  margin-bottom: 0.75rem;

  p {
    margin: 0;
    font-family: $font-barlow;
    font-size: 0.875rem;
    line-height: 1.42857143;
  }
}

.app-card-large__content-text {
  // @include media-breakpoint-up(md) {
  //   display: flex;
  //   justify-content: space-between;
  //   align-items: baseline;

  //   p {
  //     font-size: 1rem;
  //     line-height: 1.5;
  //     margin: 0 2.5rem 0 0;

  //     &:last-child {
  //       margin-right: 0;
  //     }
  //   }
  // }
}

.app-card-large__like-button {
  display: none;
}

.app-card-large__link {
  display: flex;
  align-items: flex-start;
  text-decoration: none !important;

  &:hover {
    .app-card-large__image {
      img {
        transform: scale(1.05);
      }

      &::after {
        opacity: 0.1;
      }
    }

    .app-card-large__content-title {
      text-decoration: underline;
    }
  }

  &:active {
    .app-card-large__image {
      &::after {
        opacity: 0.65;
      }
    }
  }
}

// .ui-card-large__content {
//   padding: 1.75rem;
// }

// .ui-card-large__image {
//   position: relative;
//   min-height: 100%;
//   overflow: hidden;

//   &::before {
//     content: '';
//     display: block;
//     padding-top: percentage(math.div(1, 1));
//   }

//   &::after {
//     content: '';
//     position: absolute;
//     top: 0;
//     left: 0;
//     display: block;
//     width: 100%;
//     height: 100%;
//     background-color: $black;
//     opacity: 0;
//   }

//   img {
//     position: absolute;
//     top: 0;
//     left: 0;
//     width: 100%;
//     height: 100%;
//     object-fit: cover;
//     object-position: center;
//   }
// }

// .ui-card-large__link {
//   display: block;
//   text-decoration: none !important;

//   &:hover {
//     .ui-card-large__image {
//       img {
//         transform: scale(1.05);
//       }

//       &::after {
//         opacity: 0.1;
//       }
//     }

//     .ui-card-large__content-title {
//       text-decoration: underline;
//     }
//   }

//   &:active {
//     .ui-card-large__image {
//       &::after {
//         opacity: 0.65;
//       }
//     }
//   }
// }
</style>
